import Button from 'antd/lib/button';
import Popconfirm from 'antd/lib/popconfirm';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import message from 'antd/lib/message';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { axiosDeleteCall, axiosGetCall } from '../../api/axiosCall';
import CommonLayout from '../../common/CommonLayout';
import DataTable from '../../common/DataTable';
import { getColumnSearchProps } from '../../common/DataTableSearchTemplate';
import { API_END_POINT, SOMETHING_WENT_WRONG } from '../../constant/constants';
import { AuthContext } from '../../context/AuthProvider';
import AddCategoryModel from './AddCategoryModel';
import EditCategoryModel from './EditCategoryModel';

const TableHeader = React.memo(({ handleAddButtonClick }) => {
  return (
    <div className='flex-row justify-end flex'>
      <Button
        type='primary'
        icon={<PlusOutlined />}
        className='border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
        onClick={handleAddButtonClick}
      >
        Add Category
      </Button>
    </div>
  );
});

TableHeader.propTypes = {
  handleAddButtonClick: PropTypes.func.isRequired,
};

const ProductCategory = React.memo(() => {
  const [categoryList, setCategoryList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [category, setCategory] = useState('');

  const { token } = useContext(AuthContext);

  const getCategoryList = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await axiosGetCall({
        apiEndPoint: API_END_POINT.GET_PRODUCT_CATEGORY_LIST,
        header: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.hideErrorFromUser) {
        setCategoryList(res.data);
      } else {
        message.error(SOMETHING_WENT_WRONG);
      }
    } catch (error) {
      console.error('Error fetching category list:', error);
      message.error(SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    getCategoryList();
  }, [getCategoryList]);

  const handleDeleteProductCategory = useCallback(
    async (record) => {
      try {
        const res = await axiosDeleteCall({
          apiEndPoint: `${API_END_POINT.DELETE_PRODUCT_CATEGORY}?ProductCategoryId=${record.productCategoryId}`,
          header: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.hideErrorFromUser) {
          message.success(res.data.message);
          getCategoryList();
        } else {
          message.error(res.error.message);
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        message.error(SOMETHING_WENT_WRONG);
      }
    },
    [token, getCategoryList],
  );

  const handleEditButtonClick = useCallback((data) => {
    setIsEditModalOpen(true);
    setCategory(data);
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'Logo',
        render: (record) => {
          return (
            <div className='h-16 w-16'>
              <img
                className='object-contain h-16 w-16'
                src={record.categoryImagePath}
                alt={record.categoryName}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className='hidden items-center justify-center h-16 w-16 bg-gray-200 rounded'>
                <span className='text-gray-500 text-xs'>No Image</span>
              </div>
            </div>
          );
        },
      },
      {
        key: 'categoryName',
        title: 'Category Name',
        dataIndex: 'categoryName',
        sortDirections: ['ascend', 'descend', 'ascend'],
        sorter: (a, b) => {
          if (a.categoryName)
            return a.categoryName.localeCompare(b.categoryName);
          return 0;
        },
        ...getColumnSearchProps('categoryName'),
      },
      {
        title: 'Other',
        render: (e, record) => (
          <div className='flex flex-row justify-around'>
            <Button
              onClick={() => {
                handleEditButtonClick(record);
              }}
              className='border-primary text-primary'
            >
              Edit
            </Button>
            <Popconfirm
              title='Delete Category'
              description={`Are you sure you want to delete "${record.categoryName}"?`}
              okText='Yes, Delete'
              okType='danger'
              cancelText='Cancel'
              onConfirm={() => handleDeleteProductCategory(record)}
            >
              <Button
                type='primary'
                danger
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        ),
      },
    ],
    [handleEditButtonClick, handleDeleteProductCategory],
  );

  const handleAddButtonClick = useCallback(() => {
    setIsModalOpen(!isModalOpen);
  }, [isModalOpen]);

  const handleEditModelCloseClick = useCallback(() => {
    setIsEditModalOpen(false);
    setCategory('');
  }, []);

  // Memoize DataTable props to prevent unnecessary re-renders
  const dataTableProps = useMemo(
    () => ({
      title: <TableHeader handleAddButtonClick={handleAddButtonClick} />,
      isTableRowEditable: false,
      data: categoryList,
      columns,
      isSpinning: isLoading,
      rowKey: 'productCategoryId',
    }),
    [categoryList, columns, isLoading, handleAddButtonClick],
  );

  return (
    <CommonLayout>
      <DataTable {...dataTableProps} />
      <AddCategoryModel
        isOpen={isModalOpen}
        handleModelClose={handleAddButtonClick}
        callback={getCategoryList}
      />
      {isEditModalOpen && (
        <EditCategoryModel
          isOpen={isEditModalOpen}
          category={category}
          callback={getCategoryList}
          handleEditModelCloseClick={handleEditModelCloseClick}
        />
      )}
    </CommonLayout>
  );
});

export default ProductCategory;
